import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { getServicePage, SERVICE_PAGES } from "@/data/serviceCatalog";
import { CITIES, comboPath } from "@/data/seoPages";
import { NAP } from "@/data/site";
import { mediaSrc, mediaThumb, videoSrc, videoPoster } from "@/lib/media";
import { track } from "@/lib/api";
import {
  CheckCircle, ArrowRight, ArrowDown, X, ArrowUpRight, CaretLeft, CaretRight, CaretDown, Phone, EnvelopeSimple,
} from "@phosphor-icons/react";
import { useSEO, serviceLd, breadcrumbLd, localBusinessLd, faqLd } from "@/lib/seo";

// Service landing page: a lead page that doubles as a portfolio Michael can
// send to customers. Section order follows the service-page brief — hero with
// two CTAs, overview + highlights, three alternating feature rows, the
// service's photo gallery, FAQ (with FAQPage schema) and a closing quote form.
// Copy lives in data/serviceLandings.js; photos come from the old-site archive.

const GALLERY_PREVIEW = 12; // about three rows of four on desktop
const PROJECT_PREVIEW = 8; // photos per project before "+N more" while collapsed

const scrollTo = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const page = getServicePage(slug);
  const [showAll, setShowAll] = useState(false);
  const [viewer, setViewer] = useState(null); // index into photos
  const dialogRef = useRef(null);

  const landing = page?.landing;
  // Pages without their own photos show a related service's work instead.
  const ownPhotos = page?.photos || [];
  // Photos are grouped by project; the viewer steps through them in that order.
  const groups = (ownPhotos.length ? page?.projects : page?.related?.projects)
    || [{ photos: ownPhotos.length ? ownPhotos : page?.related?.photos || [] }];
  const photos = groups.flatMap((g) => g.photos);

  useSEO({
    title: landing?.metaTitle || (page ? `${page.name} in Calgary | MRE Developments` : "Services | MRE Developments"),
    description: landing?.metaDescription || (page ? page.intro.slice(0, 155) : ""),
    path: page ? `/services/${page.slug}` : "/services",
    image: page?.hero ? mediaSrc(page.hero) : undefined,
    jsonLd: page
      ? [
          serviceLd(page.name, landing?.summary || page.intro),
          localBusinessLd(),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: page.name, path: `/services/${page.slug}` },
          ]),
          ...(landing?.faqs?.length ? [faqLd(landing.faqs)] : []),
        ]
      : [],
  });

  const closeViewer = useCallback(() => {
    dialogRef.current?.close();
    setViewer(null);
  }, []);
  const step = useCallback(
    (d) => setViewer((i) => (i === null ? i : (i + d + photos.length) % photos.length)),
    [photos.length]
  );
  useEffect(() => {
    if (viewer !== null && !dialogRef.current?.open) dialogRef.current?.showModal();
  }, [viewer]);
  useEffect(() => {
    if (viewer === null) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer, step]);

  if (!page) return <Navigate to="/services" replace />;

  const name = page.name;
  const related = SERVICE_PAGES.filter((p) => p.group === page.group && p.slug !== page.slug).slice(0, 4);
  // Collapsed: whole projects until ~12 photos are shown, each capped at PROJECT_PREVIEW.
  let start = 0, count = 0;
  const visibleGroups = [];
  for (const g of groups) {
    if (!showAll && visibleGroups.length && count >= GALLERY_PREVIEW) break;
    const limit = showAll ? g.photos.length : Math.min(g.photos.length, PROJECT_PREVIEW);
    visibleGroups.push({ ...g, start, shown: g.photos.slice(0, limit), more: g.photos.length - limit });
    start += g.photos.length; count += limit;
  }
  const hidden = photos.length - visibleGroups.reduce((n, g) => n + g.shown.length, 0);
  const features = page.features || landing?.features || [];
  const faqs = landing?.faqs || [];
  const highlights = landing?.highlights || page.blocks.find((b) => b.type === "list")?.items?.slice(0, 6) || [];
  const videos = page.videos || [];
  // Construction is local; branding, web, marketing and AI are delivered Canada-wide.
  const isDigital = page.group === "Digital & AI";
  const areaCities = isDigital
    ? ["Calgary", "Edmonton", "Vancouver", "Toronto", "Ottawa", "Montreal"]
    : NAP.serviceArea.constructionCities;

  return (
    <div data-testid="service-detail">
      {/* 1 — Hero */}
      <section className="relative min-h-[min(85vh,900px)] flex items-end border-b border-border overflow-hidden" data-testid="service-hero">
        {page.hero && (
          <img
            src={mediaSrc(page.hero)}
            alt={page.heroAlt || landing?.heroAlt || `${name} project by MRE Developments in Calgary`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/20" />
        <div className="relative w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-36 pb-16 lg:pb-24">
          <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <CaretRight size={11} />
            <Link to="/services" className="hover:text-primary transition-colors">Services</Link>
            <CaretRight size={11} />
            <span className="text-primary">{name}</span>
          </nav>
          <Reveal as="h1" className="font-display uppercase tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.92] max-w-5xl text-balance">
            {landing?.headline || `${name} in Calgary`}
          </Reveal>
          <Reveal delay={100} className="mt-6 max-w-2xl text-foreground/85 text-base sm:text-xl leading-relaxed">
            {landing?.subheadline || page.intro}
          </Reveal>
          <Reveal delay={180} className="mt-9 flex flex-col sm:flex-row gap-3">
            <a
              href="#quote"
              onClick={(e) => {
                track("cta_click", { cta: "free_quote", location: "service_hero", service: page.slug });
                scrollTo("quote")(e);
              }}
              data-testid="service-hero-quote"
              className="group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-8 h-14 hover:bg-[hsl(16,100%,55%)] transition-colors"
            >
              Get a Free Quote <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            {(photos.length > 0 || videos.length > 0) && (
              <a
                href="#gallery"
                onClick={(e) => {
                  track("cta_click", { cta: "view_gallery", location: "service_hero", service: page.slug });
                  scrollTo("gallery")(e);
                }}
                data-testid="service-hero-gallery"
                className="inline-flex items-center justify-center gap-3 border border-foreground/30 bg-background/40 backdrop-blur text-foreground font-mono-tech text-xs tracking-widest uppercase px-8 h-14 hover:border-primary hover:text-primary transition-colors"
              >
                View {name} Gallery <ArrowDown size={16} />
              </a>
            )}
          </Reveal>
        </div>
      </section>

      {/* 2 — Overview + key highlights */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16" data-testid="service-overview">
        <div className="lg:col-span-7">
          <SectionHeading overline="Overview" title={landing?.overviewTitle || `${name} across Calgary`} />
          <Reveal delay={100} className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">
            {landing?.summary || page.intro}
          </Reveal>
        </div>
        {highlights.length > 0 && (
          <Reveal delay={150} className="lg:col-span-5 border border-border bg-card p-7 self-start">
            <p className="font-mono-tech text-[11px] tracking-widest uppercase text-primary mb-5">Key highlights</p>
            <ul className="space-y-3.5" data-testid="service-highlights">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-primary shrink-0 mt-0.5" weight="fill" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        )}
      </section>

      {/* 3 — Features & benefits, alternating image side */}
      {features.length > 0 && (
        <section className="border-t border-border" data-testid="service-features">
          {features.map((f, i) => (
            <div key={f.title} className="border-b border-border last:border-b-0">
              <div className={`max-w-[1400px] mx-auto px-5 sm:px-8 py-14 lg:py-20 grid gap-10 lg:gap-16 items-center ${f.image ? "lg:grid-cols-2" : ""}`}>
                {f.image && (
                  <Reveal className={`relative aspect-[4/3] overflow-hidden bg-secondary ${i % 2 ? "lg:order-2" : ""}`}>
                    <img src={mediaSrc(f.image)} alt={f.imageAlt || `${name} by MRE Developments`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  </Reveal>
                )}
                <div className={f.image ? (i % 2 ? "lg:order-1" : "") : "max-w-3xl"}>
                  <Reveal className="font-mono-tech text-xs tracking-widest uppercase text-primary mb-4">
                    {String(i + 1).padStart(2, "0")} / {String(features.length).padStart(2, "0")}
                  </Reveal>
                  <Reveal as="h2" delay={60} className="font-display uppercase tracking-tight text-3xl sm:text-4xl leading-[0.95]">
                    {f.title}
                  </Reveal>
                  <Reveal delay={120} className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                    {f.body}
                  </Reveal>
                  {f.benefit && (
                    <Reveal delay={180} className="mt-6 border-l-2 border-primary pl-4 text-foreground">
                      {f.benefit}
                    </Reveal>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Imported page copy, for services without written landing copy */}
      {!landing && page.blocks.length > 0 && (
        <section className="border-t border-border">
          <article className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
            {page.blocks.map((b, i) => {
              if (b.type === "h") {
                return <h2 key={i} className="font-display uppercase tracking-tight text-2xl mt-10 first:mt-0">{b.text}</h2>;
              }
              if (b.type === "list") {
                return (
                  <ul key={i} className="mt-6 grid sm:grid-cols-2 gap-3">
                    {b.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-muted-foreground">
                        <CheckCircle size={20} className="text-primary shrink-0 mt-0.5" weight="light" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              return <p key={i} className="mt-5 text-muted-foreground leading-relaxed">{b.text}</p>;
            })}
          </article>
        </section>
      )}

      {/* 4 — Portfolio gallery */}
      {(photos.length > 0 || videos.length > 0) && (
        <section id="gallery" className="scroll-mt-20 border-t border-border bg-secondary/20" data-testid="service-gallery-section">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24">
            {ownPhotos.length || !page.related ? (
              <SectionHeading
                overline={photos.length ? `Portfolio · ${photos.length} photos` : "Portfolio"}
                title={`Our ${name} work`}
                intro={landing?.galleryIntro || `Recent ${name.toLowerCase()} projects by MRE Developments across Calgary and the surrounding region.`}
              />
            ) : (
              <SectionHeading
                overline={`Related work · ${photos.length} photos`}
                title={`From our ${page.related.name} projects`}
                intro={<>Project photos from related MRE work. See the full <Link to={`/services/${page.related.slug}`} className="text-primary underline underline-offset-4">{page.related.name}</Link> page.</>}
              />
            )}
            {visibleGroups.map((g, gi) => (
              <div key={g.title || gi} className="mt-10" data-testid="service-project">
                {g.title && (
                  <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-border pb-3">
                    <h3 className="font-display uppercase tracking-tight text-xl sm:text-2xl">
                      <span className="text-primary font-mono-tech text-xs tracking-widest mr-3 align-middle">{String(gi + 1).padStart(2, "0")}</span>
                      {g.title}
                    </h3>
                    <span className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground">
                      {[g.dates, `${g.photos.length} photo${g.photos.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                    </span>
                    {g.summary && <p className="w-full text-sm text-muted-foreground">{g.summary}</p>}
                  </div>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="service-gallery">
                  {g.shown.map((p, j) => {
                    const i = g.start + j;
                    const isMore = g.more > 0 && j === g.shown.length - 1;
                    return (
                      <button
                        key={p.src}
                        onClick={() => setViewer(isMore ? i + 1 : i)}
                        className="group relative aspect-square overflow-hidden bg-background"
                        aria-label={isMore ? `Show ${g.more} more photos from this project` : `Open photo ${i + 1} of ${photos.length}${p.caption ? `: ${p.caption}` : ""}`}
                      >
                        <img
                          src={mediaThumb(p.src)}
                          alt={p.alt || `${name} project photo ${i + 1} by MRE Developments, Calgary`}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {isMore ? (
                          <span className="absolute inset-0 grid place-items-center bg-black/60 font-display text-3xl text-white">+{g.more}</span>
                        ) : p.caption && (
                          <span className="absolute inset-x-0 bottom-0 p-3 text-left text-xs sm:text-sm text-white bg-gradient-to-t from-black/85 to-transparent translate-y-full group-hover:translate-y-0 group-focus-visible:translate-y-0 transition-transform duration-300">
                            {p.caption}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {(hidden > 0 || showAll) && photos.length > GALLERY_PREVIEW && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => {
                    track("cta_click", { cta: showAll ? "gallery_collapse" : "gallery_show_all", service: page.slug });
                    setShowAll((v) => !v);
                  }}
                  data-testid="service-gallery-toggle"
                  className="inline-flex items-center gap-2 border border-border px-6 h-12 font-mono-tech text-xs tracking-widest uppercase hover:border-primary hover:text-primary transition-colors"
                >
                  {showAll ? "Show fewer photos" : groups.length > 1 ? `Show all ${groups.length} projects · ${photos.length} photos` : `Show all ${photos.length} photos`}
                  <CaretDown size={14} className={`transition-transform ${showAll ? "rotate-180" : ""}`} />
                </button>
              </div>
            )}
            {videos.length > 0 && (
              <div className={`mt-12 grid gap-4 ${videos.length > 1 ? "lg:grid-cols-2" : "max-w-4xl"}`} data-testid="service-videos">
                {videos.map((v) => (
                  <video
                    key={v}
                    src={videoSrc(v)}
                    poster={videoPoster(v)}
                    controls
                    playsInline
                    preload="none"
                    className="w-full aspect-video bg-black border border-border"
                    aria-label={`${name} project video`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 5 — FAQ */}
      {faqs.length > 0 && (
        <section className="border-t border-border" data-testid="service-faq">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <SectionHeading overline="FAQ" title={`${name} questions`} />
            </div>
            <div className="lg:col-span-8 divide-y divide-border border-y border-border">
              {faqs.map((f) => (
                <details key={f.q} className="group py-5">
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none font-display uppercase tracking-tight text-lg sm:text-xl">
                    {f.q}
                    <CaretDown size={18} className="shrink-0 mt-1 text-primary transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service area + related services (internal links) */}
      <section className="border-t border-border bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-2 gap-10">
          <div>
            <p className="font-mono-tech text-[11px] tracking-widest uppercase text-primary mb-4">Service area</p>
            <div className="flex flex-wrap gap-2.5">
              {CITIES.filter((city) => areaCities.includes(city.name)).map((city) => (
                <Link key={city.slug} to={comboPath(page.slug, city.slug)} className="border border-border px-3.5 py-2.5 text-sm hover:border-primary hover:text-primary">
                  {name} in {city.name}
                </Link>
              ))}
            </div>
          </div>
          {related.length > 0 && (
            <div>
              <p className="font-mono-tech text-[11px] tracking-widest uppercase text-primary mb-4">Related services</p>
              <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
                {related.map((r) => (
                  <Link key={r.slug} to={`/services/${r.slug}`} className="flex items-center justify-between gap-3 p-4 bg-background hover:bg-secondary/40 transition-colors group">
                    <span className="font-display uppercase tracking-tight group-hover:text-primary transition-colors">{r.name}</span>
                    <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-primary shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 6 — Final CTA with quote form */}
      <section id="quote" className="scroll-mt-20 relative border-t border-border overflow-hidden grain" data-testid="service-quote">
        <div className="absolute inset-0 blueprint-grid opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12">
          <div>
            <Reveal as="h2" className="font-display uppercase tracking-tight text-3xl sm:text-5xl leading-[0.95] text-balance">
              {landing?.ctaTitle || `Ready to start your ${name.toLowerCase()} project?`}
            </Reveal>
            <Reveal delay={100} className="mt-5 text-muted-foreground text-base sm:text-lg max-w-xl">
              {landing?.ctaSubtitle || "Tell us about your project and we'll follow up within one business day to talk through scope and a project-specific quote."}
            </Reveal>
            <div className="mt-8 space-y-3">
              <a href={`tel:${NAP.phoneHref}`} onClick={() => track("click_to_call", { location: "service_quote", service: page.slug })} className="flex items-center gap-3 text-lg hover:text-primary transition-colors">
                <Phone size={20} className="text-primary" /> Call / text {NAP.phoneDisplay}
              </a>
              <a href={`tel:${NAP.phoneHref2}`} onClick={() => track("click_to_call", { location: "service_quote", service: page.slug })} className="flex items-center gap-3 text-lg hover:text-primary transition-colors">
                <Phone size={20} className="text-primary" /> Call / text {NAP.phoneDisplay2}
              </a>
              <a href={`mailto:${NAP.email}`} className="flex items-center gap-3 text-lg hover:text-primary transition-colors">
                <EnvelopeSimple size={20} className="text-primary" /> {NAP.email}
              </a>
            </div>
          </div>
          <div className="border border-border bg-card p-6 sm:p-8">
            <p className="font-display uppercase tracking-tight text-2xl mb-6">Get a free quote</p>
            <LeadForm defaultTrack={isDigital ? "grow" : "build"} source={`service_${page.slug.replace(/-/g, "_")}`} projectType={name} />
          </div>
        </div>
      </section>

      {/* Photo viewer */}
      {viewer !== null && photos[viewer] && (
        <dialog
          ref={dialogRef}
          aria-label={`${name} photo viewer`}
          onCancel={(event) => { event.preventDefault(); closeViewer(); }}
          onClick={(event) => { if (event.target === event.currentTarget) closeViewer(); }}
          className="fixed inset-0 z-50 bg-background/95 backdrop:bg-black/85 p-4 sm:p-6 max-w-[96vw] max-h-[96vh]"
          data-testid="service-lightbox"
        >
          <button autoFocus onClick={closeViewer} className="absolute top-4 right-4 z-10 bg-background p-2 text-foreground hover:text-primary" aria-label="Close photo viewer">
            <X size={26} />
          </button>
          <figure>
            <img
              src={mediaSrc(photos[viewer].src)}
              alt={photos[viewer].alt || `${name} project photograph`}
              className="max-w-full max-h-[80vh] mx-auto object-contain"
            />
            <figcaption className="mt-3 flex items-center justify-between gap-4 text-sm text-muted-foreground">
              <span>{[groups.find((g) => g.photos.includes(photos[viewer]))?.title, photos[viewer].caption].filter(Boolean).join(" — ") || name}</span>
              <span className="font-mono-tech text-xs">{viewer + 1} / {photos.length}</span>
            </figcaption>
          </figure>
          {photos.length > 1 && (
            <>
              <button onClick={() => step(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 p-2 hover:text-primary" aria-label="Previous photo">
                <CaretLeft size={26} />
              </button>
              <button onClick={() => step(1)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 p-2 hover:text-primary" aria-label="Next photo">
                <CaretRight size={26} />
              </button>
            </>
          )}
        </dialog>
      )}
    </div>
  );
}
